import React, { useState } from 'react';
import LoginPopup from './LoginPopup'; 
import logo from '../assest/PITlogo.svg'; 
import { useAuth } from '../context/AuthContext'; 
import { LogOut, User as UserIcon, ChevronDown, Menu, X, TrendingUp } from 'lucide-react'; 

// --- INTERFACES ---
interface User {
    name: string;
    email: string;
}

interface CategoryItem {
  name: string;
  href: string;
  subItems?: CategoryItem[]; 
}

// --- MOCK DATA ---
const navLinks: CategoryItem[] = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Certificate", href: "/certificate" },
    { name: "Online Registration", href: "/register" },
];

const courseCategories: CategoryItem[] = [
  {
    name: "Web Development",
    href: "/web-dev",
    subItems: [
      { name: "Java Full Stack", href: "/java-full-stack" },
      { name: "Python Full Stack", href: "/python-full-stack" },
      { name: "Mern Full Stack", href: "/mern-full-stack" },
      { name: "Backend Development", href: "/backend-developer" },
      { name: "Frontend Development", href: "/frontend-developer" },
      { name: "ReactJS Development", href: "/react-js", },
    ]
  },
  {
    name: "App Development",
    href: "/app-dev",
    subItems: [
      { name: "Android", href: "/android" },
      { name: "IOS", href: "/ios" },
      { name: "Flutter", href: "/flutter" },
    ]
  },
  {
    name: "Programming",
    href: "/programming",
    subItems: [
      { name: "C Language", href: "/programming/c" },
      { name: "Core Java", href: "/programming/core-java" },
      { name: "Python", href: "/programming/python" },
      { name: "Spring Boot", href: "/programming/springboot" }
    ]
  },
  {
    name: "Database",
    href: "/database",
    subItems: [
      { name: "SQL", href: "/database/sql" },
      { name: "MySQL", href: "/database/mysql" },
      { name: "MongoDB", href: "/database/mongodb" },
    ]
  }
];

const careerOptions: CategoryItem[] = [
    { name: "Free Guidance", href: "/guide" },
    { name: "Chat with AI expert", href: "/chat-ai" },
    { name: "Success Stories", href: "/success" },
    { name: "Contact", href: "/contact" },
];

const resourceOptions: CategoryItem[] = [
    { name: "Notes", href: "/resources/notes" },
    { name: "Videos", href: "/resources/videos" },
    { name: "Interview Questions", href: "/resources/interview" },
];

const growOptions: CategoryItem[] = [
    { name: "With Web", href: "/grow/with-web" },
    { name: "With App", href: "/grow/with-app" },
];

interface HeaderProps {
    onRegistrationClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

// --- SUB-COMPONENTS ---

const NavLink: React.FC<{ 
    href: string; 
    children: React.ReactNode; 
    onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void; 
}> = ({ href, children, onClick }) => (
  <a
    href={href}
    onClick={onClick} 
    title={`Go to ${children}`}
    className="text-gray-700 hover:text-red-600 transition duration-200 font-medium text-sm whitespace-nowrap"
  >
    {children}
  </a>
);

const DropdownMenu: React.FC<{ title: string; items: CategoryItem[]; className?: string; isNested?: boolean; icon?: React.ReactNode }> = ({ title, items, className = 'w-48', isNested = false, icon }) => {
    const [hoveredCategory, setHoveredCategory] = useState<CategoryItem | null>(null);
    const activeContent = hoveredCategory || items.find(i => i.subItems);

    return (
        <div className="relative group h-full flex items-center" onMouseLeave={() => setHoveredCategory(null)}>
            <button aria-label={`Open ${title} Menu`} aria-haspopup="true" className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition duration-200 font-medium text-sm">
                <span>{title}</span>
                <ChevronDown size={14} aria-hidden="true" />
            </button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 pt-2 flex invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 z-50">
                <div className={`bg-white p-2 rounded-xl shadow-xl border border-gray-200 ${className}`}>
                    <ul className="space-y-1">
                        {items.map((item) => (
                            <li key={item.name} onMouseEnter={() => isNested && setHoveredCategory(item)}>
                                <a href={item.href} title={`Explore ${item.name}`} className={`flex justify-between items-center w-full text-sm px-3 py-2 rounded-lg font-medium ${item.name === activeContent?.name ? 'bg-red-100 text-red-700' : 'text-gray-700 hover:bg-red-50'}`}>
                                    <span>{item.name}</span>
                                    {isNested && item.subItems && <ChevronDown size={14} className="-rotate-90" aria-hidden="true" />}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                {isNested && activeContent?.subItems && (
                    <div className="bg-white p-2 rounded-xl shadow-xl border border-gray-200 w-64 ml-2">
                        <div className="text-gray-800 text-xs font-bold px-3 py-2 border-b mb-1 uppercase tracking-wider">{activeContent.name}</div>
                        <ul className="space-y-1">
                            {activeContent.subItems.map(sub => (
                                <li key={sub.name}>
                                    <a href={sub.href} title={`Learn ${sub.name}`} className="block text-sm px-3 py-2 rounded-lg text-red-700 hover:bg-red-50 font-medium">{sub.name}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- MAIN HEADER ---
const Header: React.FC<HeaderProps> = ({ onRegistrationClick }) => {
    const { user, logout } = useAuth(); 
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false); 
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

    return (
        <>
        <div className="fixed top-0 left-0 w-full z-50 shadow-md">
            <div className="bg-red-900 text-white text-[10px] sm:text-xs font-medium h-8 flex items-center justify-center px-4" role="alert">
                <p>Limited Time Offer : Get 50% off on all certification courses!</p>
            </div>

            <header className="w-full bg-white h-16 border-b border-gray-200" role="banner">
                <div className="container mx-auto flex justify-between items-center h-full px-4 sm:px-6 lg:px-8">
                    <a href="/" className="flex-shrink-0" aria-label="Project Infotech Home">
                        <img src={logo} alt="Project Infotech - Best IT Training and Certification Institute" className="h-10 w-auto" />
                    </a>

                    <nav className="hidden xl:flex space-x-6 items-center h-full" role="navigation" aria-label="Main Navigation">
                        {navLinks.map(link => (
                            <NavLink key={link.name} href={link.href} onClick={link.name === "Online Registration" ? onRegistrationClick : undefined}>{link.name}</NavLink>
                        ))}
                        <DropdownMenu title="Courses" items={courseCategories} className="w-56" isNested={true} />
                        <DropdownMenu title="Career Guidance" items={careerOptions} isNested={false} />
                        <DropdownMenu title="Resources" items={resourceOptions} className="w-52" isNested={false} />
                        <DropdownMenu title="Grow" items={growOptions} className="w-48" isNested={false} />
                    </nav>

                    <div className="flex items-center space-x-4">
                        {user ? (
                            <div className="relative">
                                <button aria-label="User Profile Menu" aria-expanded={isProfileDropdownOpen} onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)} className="flex items-center space-x-2">
                                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold border border-red-200">{user.name.charAt(0).toUpperCase()}</div>
                                    <ChevronDown size={16} aria-hidden="true" />
                                </button>
                                {isProfileDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 border border-gray-100 z-50">
                                        <div className="px-4 py-3 border-b bg-gray-50/50">
                                            <p className="text-sm font-bold text-gray-900 truncate">{user.name}</p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <a href="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center"><UserIcon size={16} className="mr-2" aria-hidden="true"/> My Profile</a>
                                        <button onClick={() => { logout(); setIsProfileDropdownOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"><LogOut size={16} className="mr-2" aria-hidden="true"/> Logout</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button aria-label="Login or Sign Up to Project Infotech" onClick={() => setShowLoginPopup(true)} className="px-4 py-2 text-red-600 border border-red-600 rounded-lg font-medium text-sm hover:bg-red-600 hover:text-white transition">Login</button>
                        )}
                        <button aria-label="Open Mobile Navigation Menu" className="xl:hidden text-gray-600" onClick={() => setIsMobileMenuOpen(true)}><Menu size={28} aria-hidden="true" /></button>
                    </div>
                </div>
            </header>
            
            <MobileSideMenu 
                isOpen={isMobileMenuOpen} 
                onClose={() => setIsMobileMenuOpen(false)} 
                onLoginClick={() => setShowLoginPopup(true)}
                onRegistrationClick={onRegistrationClick}
                user={user}
                onLogout={logout}
                navLinks={navLinks}
                courses={courseCategories}
                career={careerOptions}
                resources={resourceOptions}
                grow={growOptions} 
            />
        </div>
        {showLoginPopup && <LoginPopup onClose={() => setShowLoginPopup(false)} onLoginSuccess={() => setShowLoginPopup(false)} />}
        </>
    );
};

// --- MOBILE MENU COMPONENT ---
interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
    onLoginClick: () => void;
    onRegistrationClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    user: User | null;
    onLogout: () => void;
    navLinks: CategoryItem[];
    courses: CategoryItem[];
    career: CategoryItem[];
    resources: CategoryItem[];
    grow: CategoryItem[]; 
}

const MobileSideMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onLoginClick, onRegistrationClick, user, onLogout, navLinks, courses, career, resources, grow }) => { 
    const [openLevel1, setOpenLevel1] = useState<string | null>(null);
    const [openLevel2, setOpenLevel2] = useState<string | null>(null);

    return (
        <>
            <div className={`fixed inset-0 bg-black/50 z-[60] transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} aria-hidden="true" />
            <div className={`fixed top-0 left-0 w-72 h-full bg-white z-[70] shadow-2xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} role="dialog" aria-modal="true" aria-label="Mobile Navigation">
                <div className="flex justify-between items-center p-4 border-b bg-red-50">
                    <span className="font-bold text-red-700 text-lg">PIT Menu</span>
                    <button aria-label="Close Mobile Menu" onClick={onClose} className="p-1 bg-white rounded-full"><X size={20} aria-hidden="true" /></button>
                </div>

                <div className="overflow-y-auto h-[calc(100vh-64px)] pb-10">
                    {user && (
                        <div className="p-4 bg-red-50 border-b border-red-100">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold">{user.name.charAt(0).toUpperCase()}</div>
                                <div className="min-w-0"><p className="text-sm font-bold truncate">{user.name}</p></div>
                            </div>
                            <button onClick={onLogout} aria-label="Logout" className="w-full py-2 bg-white text-red-600 border border-red-200 rounded-lg text-xs font-bold">LOGOUT</button>
                        </div>
                    )}

                    <nav className="divide-y divide-gray-100" aria-label="Mobile Main Navigation">
                        {navLinks.map((link: CategoryItem) => (
                            <a key={link.name} href={link.href} title={link.name} onClick={(e) => { if(link.name === "Online Registration") onRegistrationClick(e); onClose(); }} className="block px-4 py-4 text-gray-800 font-medium">{link.name}</a>
                        ))}

                        <div>
                            <button aria-expanded={openLevel1 === 'Courses'} onClick={() => setOpenLevel1(openLevel1 === 'Courses' ? null : 'Courses')} className="flex justify-between items-center w-full px-4 py-4 font-medium text-gray-800">
                                <span>Courses</span>
                                <ChevronDown size={18} className={openLevel1 === 'Courses' ? 'rotate-180' : ''} aria-hidden="true" />
                            </button>
                            {openLevel1 === 'Courses' && (
                                <div className="bg-gray-50 border-t">
                                    {courses.map((cat: CategoryItem) => (
                                        <div key={cat.name} className="border-b border-gray-200 last:border-0">
                                            <button aria-expanded={openLevel2 === cat.name} onClick={() => setOpenLevel2(openLevel2 === cat.name ? null : cat.name)} className="flex justify-between items-center w-full px-6 py-3 text-sm font-semibold text-gray-700">
                                                {cat.name}
                                                <ChevronDown size={14} className={openLevel2 === cat.name ? 'rotate-180' : ''} aria-hidden="true" />
                                            </button>
                                            {openLevel2 === cat.name && cat.subItems && (
                                                <div className="bg-white pl-8 py-2 border-t">
                                                    {cat.subItems.map((sub: CategoryItem) => (
                                                        <a key={sub.name} href={sub.href} title={`Learn ${sub.name}`} onClick={onClose} className="block py-2 text-sm text-red-600">{sub.name}</a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {[ 
                            {t: "Career Guidance", d: career}, 
                            {t: "Resources", d: resources},
                            {t: "Grow", d: grow, highlight: true } 
                        ].map((m) => (
                            <div key={m.t}>
                                <button aria-expanded={openLevel1 === m.t} onClick={() => setOpenLevel1(openLevel1 === m.t ? null : m.t)} className={`flex justify-between items-center w-full px-4 py-4 font-medium ${m.highlight ? 'text-red-700' : 'text-gray-800'}`}>
                                    <span className={m.highlight ? 'flex items-center gap-2' : ''}>
                                        {m.highlight && <TrendingUp size={16} aria-hidden="true"/>} 
                                        {m.t}
                                    </span>
                                    <ChevronDown size={18} className={openLevel1 === m.t ? 'rotate-180' : ''} aria-hidden="true" />
                                </button>
                                {openLevel1 === m.t && (
                                    <div className="bg-gray-50 border-t">
                                        {m.d.map((item: CategoryItem) => (
                                            <a key={item.name} href={item.href} title={item.name} onClick={onClose} className="block px-8 py-3 text-sm text-gray-700 border-b last:border-0">{item.name}</a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>

                    {!user && (
                        <div className="p-4 mt-4">
                            <button aria-label="Login or Sign Up" onClick={() => { onLoginClick(); onClose(); }} className="w-full py-3 bg-red-600 text-white rounded-lg font-bold">Login / Sign Up</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Header;